/** Identity of the round-scoped state currently owned by the composition root. */
export interface RoundEpochState {
  round: number;
  epoch: number;
}

/**
 * The ordered effects that replace one round's state with another's.
 *
 * `retire` receives the new epoch first, so outstanding work becomes stale before the client is
 * rebound or any state is cleared. The other steps deliberately stay synchronous: orchestration
 * starts new asynchronous reads only after this transaction has established their identity.
 */
export interface RoundRebindSteps {
  retire(nextEpoch: number): void;
  rebind(round: number, nextEpoch: number): void;
  reset(round: number, nextEpoch: number): void;
  clear(round: number, nextEpoch: number): void;
  reseed(round: number, nextEpoch: number): void;
}

/** Perform the round-boundary transaction, idempotently by reported round identity. */
export function rebindRound(
  current: RoundEpochState,
  reportedRound: number,
  steps: RoundRebindSteps,
): RoundEpochState {
  if (reportedRound === current.round) {
    return current;
  }
  const next = { round: reportedRound, epoch: current.epoch + 1 };
  steps.retire(next.epoch);
  steps.rebind(reportedRound, next.epoch);
  steps.reset(reportedRound, next.epoch);
  steps.clear(reportedRound, next.epoch);
  steps.reseed(reportedRound, next.epoch);
  return next;
}
